// 261010 Epic 2 / P2b-3 — KHUNG chung của hai bước SỬA TAY (Kịch bản, Đạo diễn): nút Sửa / Lưu / Huỷ theo `workflowActionPolicy`, nháp, báo "đang có nháp" lên trang,
// lưu qua `saveEventEdit` (kiểm job → đọc lại → so dấu vân tay → batch-edit), khối lỗi + khối "Đã lưu … đã cũ". Hai bước chỉ cung cấp chế độ xem và trình sửa.
// KHÔNG chạy AI, không gen ảnh, không tự đồng bộ sau lưu (Quyết định 5): chỉ HIỆN bước nào vừa thành cũ kèm liên kết sang bước 7 / 8.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Popconfirm, Space, message } from 'antd';
import { filmApi } from '../../services/api/film';
import type { EventPlan, FilmEvent } from '../../types/film';
import { useAliveRef } from '../../hooks/useAliveRef';
import {
  actionOf, actionsFor, JOBS_UNKNOWN_TEXT, planWriteBlock, workflowActionError, type ActionError, type WorkflowActionCtx,
} from './workflowActionPolicy';
import { cloneState, findEventIn, isDirty, saveEventEdit, snapshotOf, staleSummaryLines, type EditState } from './workflowEditOps';
import { StepErrorAlert, type InteractiveProps } from './WorkflowStepShared';
import './storyboard.css'; // `sb-scope` (biến màu) + `sb-shotedit` / `se-*` của thẻ shot

export interface EditorApi {
  draft: EditState;
  /** Đổi nháp: `fn` được chạy trên BẢN SAO rồi thay nháp (an toàn với StrictMode). */
  update: (fn: (d: EditState) => void) => void;
  /** Bản đã tải lúc mở trình sửa (để đánh dấu shot nào đang sửa). */
  base: EditState;
  saving: boolean;
  /** Bank của plan lúc mở trình sửa (danh sách nhân vật để chọn). */
  bank: EventPlan['refBank'] | undefined;
  /** 261010 P9D — nạp lại bản gốc từ server (sau thao tác cấu trúc gọi thẳng route: id shot đổi). Chỉ gọi khi KHÔNG có nháp chưa lưu. */
  reopen: () => Promise<void>;
}

interface Props extends InteractiveProps {
  stepKey: 'SCRIPT' | 'DIRECTING' | 'CINEMATOGRAPHY';
  eventId: string;
  ev: FilmEvent;
  detail: EventPlan | null;
  /** Chế độ xem — nhận bản event mới nhất (sau Lưu dùng ngay bản server trả về, không đợi trang nạp lại). */
  view: (ev: FilmEvent) => React.ReactNode;
  renderEditor: (api: EditorApi) => React.ReactNode;
  /** 261010 P8c — thanh chạy Quay phim (giữa khối lỗi và hàng nút). Không truyền ⇒ DOM bước 1/3 y nguyên. */
  extra?: (s: { editing: boolean; dirty: boolean }) => React.ReactNode;
}

const busyError = (text: string): ActionError => ({ kind: 'BUSY', text, reload: true });

const WorkflowEditFrame: React.FC<Props> = ({ stepKey, planId, eventId, ev, detail, stepState, jobs, onActionDone, onJobsRefresh, setEditDirty, onGoStep, view, renderEditor, extra }) => {
  const alive = useAliveRef();
  const [base, setBase] = useState<EditState | null>(null);
  const [draft, setDraft] = useState<EditState | null>(null);
  const [bank, setBank] = useState<EventPlan['refBank'] | undefined>(undefined);
  const [opening, setOpening] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<ActionError | null>(null);
  const [summary, setSummary] = useState<{ lines: string[]; extra: string[]; prompt: boolean; clip: boolean } | null>(null);
  const [override, setOverride] = useState<FilmEvent | null>(null); // bản server trả về sau Lưu, cho tới khi trang nạp lại chi tiết

  const editing = !!base && !!draft;
  const dirty = useMemo(() => (base && draft ? isDirty(base, draft) : false), [base, draft]);

  useEffect(() => { setEditDirty?.(dirty); }, [dirty, setEditDirty]);
  useEffect(() => () => { setEditDirty?.(false); }, [setEditDirty]);
  // Trang nạp lại chi tiết xong (ev đổi) ⇒ bỏ bản tạm sau Lưu.
  useEffect(() => { setOverride(null); }, [ev]);

  const prefix = stepKey;
  const ctx: WorkflowActionCtx = useMemo(() => ({ isSky: true, stepState, runningJobKinds: [], edit: { eventId, editing, dirty, jobs } }), [stepState, eventId, editing, dirty, jobs]);
  const actions = useMemo(() => actionsFor(stepKey, ctx), [stepKey, ctx]);
  const editA = actionOf(actions, `${prefix}_EDIT`);
  const saveA = actionOf(actions, `${prefix}_SAVE`);
  const discardA = actionOf(actions, `${prefix}_DISCARD`);

  const update = useCallback((fn: (d: EditState) => void) => {
    setDraft((prev) => { if (!prev) return prev; const n = cloneState(prev); fn(n); return n; });
  }, []);

  /** Vào trình sửa: nạp lại `workflow-status` và `/events` NGAY (bản chi tiết của trang có thể cũ), kiểm job, chụp bản gốc. */
  const openEditor = async () => {
    setErr(null); setSummary(null); setOpening(true);
    try {
      const st = await onJobsRefresh();
      if (!alive.current) return;
      if (!st) { setErr(busyError(JOBS_UNKNOWN_TEXT)); return; }
      const block = planWriteBlock(st.jobs, eventId);
      if (block.blocked) { setErr(busyError(block.reason || JOBS_UNKNOWN_TEXT)); return; }
      const plan = await filmApi.getEventPlan(planId);
      if (!alive.current) return;
      const fresh = findEventIn(plan, eventId);
      if (!fresh) { setErr(workflowActionError({ response: { data: { code: 'NOT_FOUND' } } }, 'Mở trình sửa')); return; }
      const snap = snapshotOf(fresh);
      setBase(snap); setDraft(cloneState(snap)); setBank(plan.refBank);
    } catch (e: any) { if (alive.current) setErr(workflowActionError(e, 'Mở trình sửa')); }
    finally { if (alive.current) setOpening(false); }
  };

  const leave = () => { setBase(null); setDraft(null); };

  const doSave = async () => {
    if (!base || !draft) return;
    setErr(null); setSaving(true);
    try {
      const r = await saveEventEdit(
        {
          refreshJobs: async () => (await onJobsRefresh())?.jobs ?? null,
          getEventPlan: (id) => filmApi.getEventPlan(id),
          batchEditPlan: (id, ops) => filmApi.batchEditPlan(id, ops),
        },
        { planId, eventId, base, draft },
      );
      if (!alive.current) return;
      if (r.kind === 'NOOP') return;
      if (r.kind === 'BLOCKED') { setErr(busyError(r.reason)); return; }
      if (r.kind === 'CHANGED_ELSEWHERE') { setErr(workflowActionError({ code: 'CHANGED_ELSEWHERE' }, 'Lưu')); return; }
      const lines = staleSummaryLines(r.delta);
      const extra: string[] = [];
      if (r.duration) {
        const manual = r.ops.some((o) => o.type === 'updateEvent' && 'genDurationS' in o);
        extra.push(`Số giây của video: ${r.duration.from ?? '?'} → ${r.duration.to ?? '?'}${manual ? ' (bạn đặt).' : ' (hệ thống tự tính lại từ nhịp và thoại khi lưu).'}${(ev.clips || []).length ? ' Clip đã có được tạo theo số giây cũ nên có thể không chọn được nữa — tạo clip mới ở bước 8.' : ''}`);
      }
      setSummary({ lines, extra, prompt: r.delta.some((d) => d.newStages.includes('prompt')), clip: r.delta.some((d) => d.newStages.includes('clip')) });
      setOverride(findEventIn(r.plan, eventId) ?? null);
      leave();
      message.success('Đã lưu');
    } catch (e: any) { if (alive.current) setErr(workflowActionError(e, 'Lưu thay đổi')); }
    finally { if (alive.current) setSaving(false); onActionDone(); }
  };

  const note = editing ? (!saveA?.enabled && dirty ? saveA?.reason : null) : (!editA?.enabled ? editA?.reason : null);
  const shown = override ?? ev;

  return (
    <div data-testid={`wf-${stepKey.toLowerCase()}-step`}>
      <StepErrorAlert error={err} onReload={() => { if (err?.kind === 'CHANGED_ELSEWHERE' || err?.kind === 'NOT_FOUND') leave(); /* bản gốc đã cũ ⇒ bỏ nháp; lỗi khác (đang bận…) thì GIỮ nháp */ onActionDone(); }} onClose={() => setErr(null)} />
      {summary && (
        <Alert type="success" showIcon closable onClose={() => setSummary(null)} style={{ marginBottom: 10 }} data-testid="wf-edit-saved"
          message="Đã lưu."
          description={(
            <div>
              {summary.lines.length ? <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{summary.lines.map((l) => <li key={l}>{l}</li>)}</ul> : <div>Không có bước nào thành cũ.</div>}
              {summary.extra.map((l) => <div key={l} style={{ marginTop: 4 }}>{l}</div>)}
              {onGoStep && (summary.prompt || summary.clip) && (
                <Space size={8} style={{ marginTop: 8 }}>
                  {summary.prompt && <Button size="small" onClick={() => onGoStep('PROMPT')}>Mở bước 7 — Prompt</Button>}
                  {summary.clip && <Button size="small" onClick={() => onGoStep('VIDEO')}>Mở bước 8 — Video</Button>}
                </Space>
              )}
            </div>
          )} />
      )}
      {extra?.({ editing, dirty })}
      <Space wrap size={8} style={{ marginBottom: 10 }}>
        {!editing && editA?.visible && (
          <Button size="small" type="primary" loading={opening} disabled={!editA.enabled} title={editA.enabled ? undefined : editA.reason} onClick={openEditor} data-testid={`wf-${stepKey.toLowerCase()}-edit`}>{editA.label}</Button>
        )}
        {editing && saveA?.visible && (
          <Button size="small" type="primary" loading={saving} disabled={!saveA.enabled} title={saveA.enabled ? undefined : saveA.reason} onClick={doSave} data-testid={`wf-${stepKey.toLowerCase()}-save`}>{saveA.label}</Button>
        )}
        {editing && discardA?.visible && (dirty
          ? <Popconfirm title="Bỏ thay đổi chưa lưu?" okText="Bỏ thay đổi" cancelText="Ở lại" disabled={saving} onConfirm={() => { leave(); setErr(null); }}><Button size="small" disabled={saving}>{discardA.label}</Button></Popconfirm>
          : <Button size="small" disabled={saving} onClick={() => { leave(); setErr(null); }}>{discardA.label}</Button>)}
        {dirty && <span className="muted" style={{ fontSize: 12 }}>● Có thay đổi chưa lưu</span>}
        {note && <span className="muted" style={{ fontSize: 13 }} data-testid="wf-edit-reason">{note}</span>}
      </Space>
      {editing && base && draft
        ? <div className="sb-scope" data-testid={`wf-${stepKey.toLowerCase()}-editor`}>{renderEditor({ draft, update, base, saving, bank: bank ?? detail?.refBank, reopen: openEditor })}</div>
        : view(shown)}
    </div>
  );
};

export default WorkflowEditFrame;
